// Administrador Master shell (ADR-0047): its own layout, never inside a tenant workspace and never the
// commercial website's administrator. Shown only to operators of Media Interactiva (the server decides:
// /me.operatorRoles); every request is re-authorized by the API and the database.
import { Link, Outlet, useRouterState } from "@tanstack/react-router";
import { ArrowLeft, Building2, ClipboardCheck, FileText, Gauge, Landmark, Receipt, Settings } from "lucide-react";
import { useMe } from "@/api/hooks";
import { cn } from "@/ui/cn";
import { EmptyState } from "@/ui/layout";
import { Brand } from "@/shell/app-shell";

const SECTIONS = [
  { to: "/admin", label: "Panel", icon: Gauge, roles: ["SUPER_ADMIN", "COMMERCIAL_ADMIN"] },
  { to: "/admin/companies", label: "Empresas", icon: Building2, roles: ["SUPER_ADMIN", "COMMERCIAL_ADMIN"] },
  {
    to: "/admin/purchase-orders",
    label: "Órdenes de compra",
    icon: FileText,
    roles: ["SUPER_ADMIN", "COMMERCIAL_ADMIN"],
  },
  {
    to: "/admin/authorizations",
    label: "Campañas pendientes",
    icon: ClipboardCheck,
    roles: ["SUPER_ADMIN", "COMMERCIAL_ADMIN"],
  },
  {
    to: "/admin/finance",
    label: "Crédito y consumo",
    icon: Landmark,
    roles: ["SUPER_ADMIN", "COMMERCIAL_ADMIN", "FINANCE_ADMIN"],
  },
  {
    to: "/admin/billing",
    label: "Facturación",
    icon: Receipt,
    roles: ["SUPER_ADMIN", "COMMERCIAL_ADMIN", "FINANCE_ADMIN"],
  },
  { to: "/admin/settings", label: "Configuración", icon: Settings, roles: ["SUPER_ADMIN", "COMMERCIAL_ADMIN"] },
] as const;

export const ROLE_NAMES: Record<string, string> = {
  SUPER_ADMIN: "Super Admin",
  COMMERCIAL_ADMIN: "Administrador comercial",
  FINANCE_ADMIN: "Administrador financiero",
};

export function useOperatorRoles(): readonly string[] {
  const { data: me } = useMe();
  return me?.operatorRoles ?? [];
}

export function AdminLayout() {
  const { data: me, isLoading } = useMe();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const roles = me?.operatorRoles ?? [];
  const sections = SECTIONS.filter((s) => s.roles.some((r) => roles.includes(r)));
  if (isLoading) return null;
  if (!me || roles.length === 0)
    return (
      <main id="main" className="grid min-h-dvh place-items-center bg-canvas p-6">
        <EmptyState
          title="Sin acceso al Administrador Master"
          description="Esta área es solo para el equipo autorizado de Media Interactiva."
          action={
            <Link className="text-sm text-brand underline" to="/">
              Volver a InteractAds
            </Link>
          }
        />
      </main>
    );
  const isActive = (to: string) =>
    to === "/admin" ? pathname === "/admin" || pathname === "/admin/" : pathname.startsWith(to);
  return (
    <div className="min-h-dvh bg-canvas">
      <header className="flex flex-wrap items-center gap-3 border-b border-line bg-surface px-4 py-2.5 sm:px-6">
        <Brand />
        <span className="rounded-sm bg-brand-soft px-2 py-0.5 text-xs font-semibold text-brand">
          Administrador Master
        </span>
        <span className="hidden text-xs text-muted sm:inline">
          Plataforma InteractAds · cada acción pide tu segundo factor
        </span>
        <span className="ml-auto truncate text-xs text-muted">
          {me.user.email} · {roles.map((r) => ROLE_NAMES[r] ?? r).join(", ")}
        </span>
        <Link to="/" className="flex items-center gap-1 text-xs text-muted hover:text-fg">
          <ArrowLeft className="size-3.5" /> Volver a tu espacio de trabajo
        </Link>
      </header>
      <div className="mx-auto grid max-w-7xl gap-6 px-4 py-6 sm:px-6 lg:grid-cols-[200px_1fr]">
        <nav aria-label="Administrador Master" className="flex gap-1 overflow-x-auto lg:flex-col">
          {sections.map((s) => (
            <Link
              key={s.to}
              to={s.to}
              className={cn(
                "flex shrink-0 items-center gap-2 rounded-md px-2.5 py-1.5 text-sm text-muted hover:bg-sunken hover:text-fg [&_svg]:size-4",
                isActive(s.to) && "bg-sunken font-medium text-fg"
              )}
            >
              <s.icon /> {s.label}
            </Link>
          ))}
        </nav>
        <main id="main" className="min-w-0">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
